import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { GuardianRule } from "@/services/api/guardiansv2/types";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import { guardiansErrorMessage } from "../caughtError";
import TextArea from "@/components/ui/form-input/TextArea";

export interface GenerateRulesDialogProps {
  catalogueKey: string | null;
  clientId?: string | null;
  onClose: () => void;
  onGenerated: (rules: GuardianRule[]) => void;
}

export function GenerateRulesDialog({ catalogueKey, clientId, onClose, onGenerated }: GenerateRulesDialogProps) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await GuardiansV2API.assistRules({
        prompt: prompt.trim(),
        catalogue_key: catalogueKey,
        client_id: clientId,
      });
      if (!result.rules.length) {
        setError("No rules were generated. Try rephrasing your description.");
        return;
      }
      onGenerated(result.rules);
      onClose();
    } catch (err) {
      setError(guardiansErrorMessage(err, "Could not generate rules"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
      data-testid="generate-rules-dialog"
    >
      <div
        className="w-full max-w-lg rounded-xl border border-slate-600 bg-slate-800 p-4 shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faWandMagicSparkles} className="text-indigo-300" />
          <h3 className="text-sm font-semibold text-slate-100">Generate rules</h3>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Describe what should raise an alert. The assistant drafts LLM check rules.
        </p>
        <TextArea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          rows={4}
          placeholder='e.g. Alert when the agent used no tools, or when its thinking mentions "unsure", or the task ended blocked'
          className="mt-3 !rounded-lg !border-slate-600 !bg-slate-900 !text-sm"
          data-testid="generate-rules-prompt"
        />
        {error ? <p className="mt-2 text-xs text-rose-300">{error}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:bg-slate-700"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading || !prompt.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-500 disabled:opacity-50"
            onClick={() => void runGenerate()}
            data-testid="generate-rules-submit"
          >
            {loading ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>
    </div>
  );
}
